// src/components/AvatarEditorPanel.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import "./AvatarSetupPanel.css"; // ✅ REUSE EXISTING CSS

import * as THREE from "three";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader.js";

const SKIN_TONES = [
  // very fair
  "#fff3e8",
  "#f8e7dc",
  "#f4ddcf",

  // light
  "#f1d0b6",
  "#e6bf9c",
  "#ddb389",

  // light/medium
  "#d1a06f",
  "#c78b5a",

  // medium
  "#a9643a",
  "#8d5524",
  "#7a4422",

  // deep
  "#5c3a1a",
  "#4a2f18",
  "#3a2414",
];

const HAIR_COLORS = [
  // blondes / platinum
  "#d2d1cc",
  "#f2e6b8",
  "#e8d39a",
  "#d2bd77",

  // strawberry / copper / red
  "#c46b3b",
  "#a24a2a",
  "#7a2f23",

  // browns
  "#6b4a2f",
  "#5a3b26",
  "#4b2f20",

  // cool dark / black
  "#2b2320",
  "#1a1a1a",

  // fun but still realistic (optional—comment out if you want)
  "#2f2a3a", // soft black-violet
  "#3a3a3a", // graphite
];

const HAIR_STYLES = Array.from({ length: 19 }, (_, i) => {
  const n = i + 1;
  return {
    id: `hair_${n}`,
    label: `Style ${n}`,
    src: `/models/wigs/hair_${n}.glb`,
  };
});

/**
 * Calibration from your Blender measurements (cm)
 * m0 = measurement when shape key value = 0
 * m1 = measurement when shape key value = 1
 */
const CAL = {
  bust: { m0: 84.0, m1: 89.0, key: "Bust_up" },
  waist: { m0: 65.5, m1: 85.4, key: "Waist_up" },
  hip: { m0: 80.7, m1: 114.7, key: "Hip_up" },

  // Optional proxies (safe to keep; leave blank inputs if you don't want them)
  arm: { m0: 30.8, m1: 46.6, key: "Arm_up" },
  leg: { m0: 45.7, m1: 62.5, key: "Leg_up" }, // thigh proxy -> Leg_up
};

// Simple AU/NZ-ish estimates (V1 only)
const SIZE_TO_CM = {
  6: { bust: 80, waist: 60, hip: 86 },
  8: { bust: 84, waist: 64, hip: 90 },
  10: { bust: 88, waist: 68, hip: 94 },
  12: { bust: 92, waist: 72, hip: 98 },
  14: { bust: 96, waist: 76, hip: 102 },
  16: { bust: 102, waist: 82, hip: 108 },
  18: { bust: 108, waist: 88, hip: 114 },
  20: { bust: 114, waist: 94, hip: 120 },
};

const SIZE_OPTIONS = Object.keys(SIZE_TO_CM)
  .map((n) => Number(n))
  .sort((a, b) => a - b);

function clamp01(x) {
  return Math.max(0, Math.min(1, x));
}

function solveT(cm, m0, m1) {
  const denom = m1 - m0;
  if (!isFinite(denom) || Math.abs(denom) < 1e-6) return 0;
  return clamp01((cm - m0) / denom);
}

function parseCm(v) {
  const n = Number(String(v ?? "").replace(",", ".").trim());
  return Number.isFinite(n) ? n : null;
}

function HairThumb({ label, selected }) {
  return (
    <div
      style={{
        width: "100%",
        height: "100%",
        display: "grid",
        placeItems: "center",
        borderRadius: 14,
        background: selected
          ? "rgba(176, 140, 108, 0.16)"
          : "rgba(251, 247, 242, 0.72)",
        color: "var(--charcoal)",
        fontSize: 12,
        fontWeight: 600,
        letterSpacing: "0.02em",
      }}
    >
      {label}
    </div>
  );
}

export default function AvatarEditorPanel({ open, avatar, setAvatar, onClose }) {
  // ✅ Hooks must ALWAYS run, even when open === false
  const [activeTab, setActiveTab] = useState("skin"); // "skin" | "hair" | "measurements"
  const [sizeTab, setSizeTab] = useState("measurements"); // "measurements" | "sizes"
  const [showDebug, setShowDebug] = useState(false);

 const [meas, setMeas] = useState(() => {
  const bustVal = Number(avatar?.[CAL.bust.key] ?? 0);
  const waistVal = Number(avatar?.[CAL.waist.key] ?? 0);
  const hipVal = Number(avatar?.[CAL.hip.key] ?? 0);
  const armVal = Number(avatar?.[CAL.arm.key] ?? 0);
  const legVal = Number(avatar?.[CAL.leg.key] ?? 0);

  const bustCm = CAL.bust.m0 + bustVal * (CAL.bust.m1 - CAL.bust.m0);
  const waistCm = CAL.waist.m0 + waistVal * (CAL.waist.m1 - CAL.waist.m0);
  const hipCm = CAL.hip.m0 + hipVal * (CAL.hip.m1 - CAL.hip.m0);
  const armCm = CAL.arm.m0 + armVal * (CAL.arm.m1 - CAL.arm.m0);
  const legCm = CAL.leg.m0 + legVal * (CAL.leg.m1 - CAL.leg.m0);

  return {
    bust: Number.isFinite(bustCm) ? bustCm.toFixed(1) : "",
    waist: Number.isFinite(waistCm) ? waistCm.toFixed(1) : "",
    hip: Number.isFinite(hipCm) ? hipCm.toFixed(1) : "",
    arm: Number.isFinite(armCm) ? armCm.toFixed(1) : "",
    leg: Number.isFinite(legCm) ? legCm.toFixed(1) : "",
  };
});

  const [topSize, setTopSize] = useState(10);
  const [pantSize, setPantSize] = useState(10);

  const estimatedFromSizes = useMemo(() => {
    const top = SIZE_TO_CM[topSize];
    const pant = SIZE_TO_CM[pantSize];
    if (!top || !pant) return null;
    return { bust: top.bust, waist: pant.waist, hip: pant.hip };
  }, [topSize, pantSize]);

  useEffect(() => {
  const bustVal = Number(avatar?.[CAL.bust.key] ?? 0);
  const waistVal = Number(avatar?.[CAL.waist.key] ?? 0);
  const hipVal = Number(avatar?.[CAL.hip.key] ?? 0);
  const armVal = Number(avatar?.[CAL.arm.key] ?? 0);
  const legVal = Number(avatar?.[CAL.leg.key] ?? 0);

  const bustCm = CAL.bust.m0 + bustVal * (CAL.bust.m1 - CAL.bust.m0);
  const waistCm = CAL.waist.m0 + waistVal * (CAL.waist.m1 - CAL.waist.m0);
  const hipCm = CAL.hip.m0 + hipVal * (CAL.hip.m1 - CAL.hip.m0);
  const armCm = CAL.arm.m0 + armVal * (CAL.arm.m1 - CAL.arm.m0);
  const legCm = CAL.leg.m0 + legVal * (CAL.leg.m1 - CAL.leg.m0);

  setMeas({
    bust: Number.isFinite(bustCm) ? bustCm.toFixed(1) : "",
    waist: Number.isFinite(waistCm) ? waistCm.toFixed(1) : "",
    hip: Number.isFinite(hipCm) ? hipCm.toFixed(1) : "",
    arm: Number.isFinite(armCm) ? armCm.toFixed(1) : "",
    leg: Number.isFinite(legCm) ? legCm.toFixed(1) : "",
  });
}, [avatar]);

  // ✅ After hooks are declared, it's safe to early-return
  if (!open) return null;

  const updateSkin = (patch) => {
    setAvatar((prev) => ({
      ...prev,
      skin: {
        ...prev.skin,
        ...patch,
      },
    }));
  };

  const updateHair = (patch) => {
    setAvatar((prev) => ({
      ...prev,
      hair: {
        ...(prev.hair ?? {}),
        ...patch,
      },
    }));
  };

  const updateShapeKey = (key, value) => {
    const v = clamp01(Number(value));
    setAvatar((prev) => ({ ...prev, [key]: v }));
  };

  const applyFromMeasurements = () => {
    const bust = parseCm(meas.bust);
    const waist = parseCm(meas.waist);
    const hip = parseCm(meas.hip);
    const arm = parseCm(meas.arm);
    const leg = parseCm(meas.leg);

    setAvatar((prev) => {
      const next = { ...prev };

      if (bust != null) next[CAL.bust.key] = solveT(bust, CAL.bust.m0, CAL.bust.m1);
      if (waist != null) next[CAL.waist.key] = solveT(waist, CAL.waist.m0, CAL.waist.m1);
      if (hip != null) next[CAL.hip.key] = solveT(hip, CAL.hip.m0, CAL.hip.m1);

      // optional proxies
      if (arm != null) next[CAL.arm.key] = solveT(arm, CAL.arm.m0, CAL.arm.m1);
      if (leg != null) next[CAL.leg.key] = solveT(leg, CAL.leg.m0, CAL.leg.m1);

      return next;
    });
  };

  const applyFromSizes = () => {
    const top = SIZE_TO_CM[topSize];
    const pant = SIZE_TO_CM[pantSize];
    if (!top || !pant) return;

    setAvatar((prev) => {
      const next = { ...prev };
      next[CAL.bust.key] = solveT(top.bust, CAL.bust.m0, CAL.bust.m1);
      next[CAL.waist.key] = solveT(pant.waist, CAL.waist.m0, CAL.waist.m1);
      next[CAL.hip.key] = solveT(pant.hip, CAL.hip.m0, CAL.hip.m1);
      return next;
    });
  };

  const DebugRow = ({ label, k }) => (
    <div style={{ display: "flex", justifyContent: "space-between", gap: 12 }}>
      <span style={{ opacity: 0.85 }}>{label}</span>
      <span style={{ opacity: 0.65, fontVariantNumeric: "tabular-nums" }}>
        {(avatar[k] ?? 0).toFixed(3)}
      </span>
    </div>
  );

  return (
    <div className="avatar-panel">
      <div className="avatar-header">
        <h2>Set your Mannequin</h2>
      </div>

      <div className="avatar-folder-tabs">
        <button
          className={`avatar-folder-tab ${activeTab === "skin" ? "is-active" : ""}`}
          onClick={() => setActiveTab("skin")}
        >
          Skin
        </button>
        <button
          className={`avatar-folder-tab ${activeTab === "hair" ? "is-active" : ""}`}
          onClick={() => setActiveTab("hair")}
        >
          Hair
        </button>
        <button
          className={`avatar-folder-tab ${activeTab === "measurements" ? "is-active" : ""}`}
          onClick={() => setActiveTab("measurements")}
        >
          Measurements
        </button>
      </div>

      <div className="avatar-body" style={{ minHeight: 500 }}>
        <div style={{ display: activeTab === "skin" ? "block" : "none" }}>
          {/* ---------- SKIN ---------- */}
          <h3 className="section-title">Skin tone</h3>

          <div className="button-row">
            {SKIN_TONES.map((tone) => (
              <button
                key={tone}
                className="select-btn"
                style={{
                  background: tone,
                  border: avatar.skin?.base === tone ? "3px solid #111" : "1px solid #ccc",
                  width: 32,
                  height: 32,
                  borderRadius: "50%",
                }}
                onClick={() => updateSkin({ base: tone })}
              />
            ))}
          </div>

          <h3 className="section-title">Undertone</h3>
          <div className="button-row">
            {["warm", "neutral", "cool"].map((u) => (
              <button
                key={u}
                className={`select-btn ${avatar.skin?.undertone === u ? "selected" : ""}`}
                onClick={() => updateSkin({ undertone: u })}
              >
                {u}
              </button>
            ))}
          </div>
        </div>

        <div style={{ display: activeTab === "hair" ? "block" : "none" }}>
          {/* ---------- HAIR ---------- */}
          <h3 className="section-title">Hair</h3>

          <div style={{ fontSize: 12, opacity: 0.75, marginBottom: 10 }}>
            Pick a style and colour
          </div>

          <h3 className="section-title" style={{ marginTop: 6 }}>
            Hair colour
          </h3>
          <div className="button-row" style={{ flexWrap: "wrap", gap: 10 }}>
            {HAIR_COLORS.map((c) => (
              <button
                key={c}
                className="select-btn"
                style={{
                  background: c,
                  border: (avatar.hair?.color ?? "") === c ? "3px solid #111" : "1px solid #ccc",
                  width: 30,
                  height: 30,
                  borderRadius: "50%",
                }}
                onClick={() => updateHair({ color: c })}
                title={c}
              />
            ))}
          </div>

          <h3 className="section-title" style={{ marginTop: 10 }}>
            Hair style
          </h3>

          <div
            className="hair-grid"
            style={{
              marginTop: 10,
            }}
          >
{activeTab === "hair" &&
  HAIR_STYLES.map((h) => {
    const selected = (avatar.hair?.style ?? "") === h.id;
    return (
      <button
        key={h.id}
        type="button"
        className={`hair-tile ${selected ? "selected" : ""}`}
        onClick={() => updateHair({ style: h.id })}
        title={h.label}
      >
        <HairThumb label={h.label} selected={selected} />
      </button>
    );
  })}
          </div>
        </div>

        <div style={{ display: activeTab === "measurements" ? "block" : "none" }}>
          {/* ---------- SIZE / MEASUREMENTS ---------- */}
          <h3 className="section-title">Sizing</h3>

          <div className="button-row" style={{ gap: 10, flexWrap: "wrap" }}>
            <button
              className={`select-btn ${sizeTab === "measurements" ? "selected" : ""}`}
              onClick={() => setSizeTab("measurements")}
            >
              Measurements
            </button>
            <button
              className={`select-btn ${sizeTab === "sizes" ? "selected" : ""}`}
              onClick={() => setSizeTab("sizes")}
            >
              I don&apos;t know
            </button>
          </div>

          {sizeTab === "measurements" && (
            <div style={{ marginTop: 10 }}>
              <div style={{ fontSize: 12, opacity: 0.75, marginBottom: 10 }}>
                Enter cm. We map these to the mannequin shape keys you measured in Blender.
              </div>

              <label>
                Bust (cm)
                <input
                  className="veya-field"
                  type="number"
                  inputMode="decimal"
                  placeholder="e.g. 88"
                  value={meas.bust}
                  onChange={(e) => setMeas((p) => ({ ...p, bust: e.target.value }))}
                />
              </label>

              <label>
                Waist (cm)
                <input
                  className="veya-field"
                  type="number"
                  inputMode="decimal"
                  placeholder="e.g. 75"
                  value={meas.waist}
                  onChange={(e) => setMeas((p) => ({ ...p, waist: e.target.value }))}
                />
              </label>

              <label>
                Hips (cm)
                <input
                  className="veya-field"
                  type="number"
                  inputMode="decimal"
                  placeholder="e.g. 98"
                  value={meas.hip}
                  onChange={(e) => setMeas((p) => ({ ...p, hip: e.target.value }))}
                />
              </label>

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                <label>
                  Arm (cm) <span style={{ opacity: 0.6 }}>(optional)</span>
                  <input
                    className="veya-field"
                    type="number"
                    inputMode="decimal"
                    placeholder="e.g. 35"
                    value={meas.arm}
                    onChange={(e) => setMeas((p) => ({ ...p, arm: e.target.value }))}
                  />
                </label>

                <label>
                  Thigh (cm) <span style={{ opacity: 0.6 }}>(optional)</span>
                  <input
                    className="veya-field"
                    type="number"
                    inputMode="decimal"
                    placeholder="e.g. 52"
                    value={meas.leg}
                    onChange={(e) => setMeas((p) => ({ ...p, leg: e.target.value }))}
                  />
                </label>
              </div>

              <div className="button-row" style={{ marginTop: 12, gap: 10 }}>
                <button className="gold-btn" onClick={applyFromMeasurements}>
                  Apply measurements
                </button>
                <button
                  className="select-btn"
                  onClick={() =>
                    setMeas({
                      bust: "",
                      waist: "",
                      hip: "",
                      arm: "",
                      leg: "",
                    })
                  }
                >
                  Clear
                </button>
              </div>
            </div>
          )}

          {sizeTab === "sizes" && (
            <div style={{ marginTop: 10 }}>
              <div style={{ fontSize: 12, opacity: 0.75, marginBottom: 10 }}>
                Pick a top size and pants size. We estimate measurements and set the mannequin.
              </div>

              <label>
                Top size
                <select
                  className="veya-field"
                  value={topSize}
                  onChange={(e) => setTopSize(Number(e.target.value))}
                >
                  {SIZE_OPTIONS.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
              </label>

              <label>
                Pants size
                <select
                  className="veya-field"
                  value={pantSize}
                  onChange={(e) => setPantSize(Number(e.target.value))}
                >
                  {SIZE_OPTIONS.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
              </label>

              {estimatedFromSizes && (
                <div
                  style={{
                    marginTop: 10,
                    padding: 10,
                    borderRadius: 12,
                    border: "1px solid rgba(176, 140, 108, 0.25)",
                    background: "rgba(251, 247, 242, 0.55)",
                    fontSize: 12,
                    opacity: 0.9,
                  }}
                >
                  Estimated measurements: <b>Bust {estimatedFromSizes.bust}cm</b>,{" "}
                  <b>Waist {estimatedFromSizes.waist}cm</b>, <b>Hips {estimatedFromSizes.hip}cm</b>
                </div>
              )}

              <div className="button-row" style={{ marginTop: 12 }}>
                <button className="gold-btn" onClick={applyFromSizes}>
                  Apply size
                </button>
              </div>
            </div>
          )}
          </div>

        <div style={{ marginTop: 18 }}>
          {showDebug && (
            <div style={{ marginTop: 10 }}>
              <h3 className="section-title">Shape keys (debug)</h3>

              <div
                style={{
                  padding: 10,
                  borderRadius: 12,
                  border: "1px solid rgba(176, 140, 108, 0.25)",
                  background: "rgba(251, 247, 242, 0.45)",
                }}
              >
                <DebugRow label="Bust_up" k="Bust_up" />
                <DebugRow label="Waist_up" k="Waist_up" />
                <DebugRow label="Hip_up" k="Hip_up" />
                <DebugRow label="Arm_up" k="Arm_up" />
                <DebugRow label="Leg_up" k="Leg_up" />
              </div>

              <div style={{ marginTop: 10 }}>
                {[
                  { key: "Arm_up", label: "Arm" },
                  { key: "Bust_up", label: "Bust" },
                  { key: "Waist_up", label: "Waist" },
                  { key: "Hip_up", label: "Hips" },
                  { key: "Leg_up", label: "Leg" },
                ].map(({ key, label }) => (
                  <label key={key}>
                    {label} <span style={{ opacity: 0.65 }}>({(avatar[key] ?? 0).toFixed(2)})</span>
                    <input
                      type="range"
                      min={0}
                      max={1}
                      step={0.01}
                      value={avatar[key] ?? 0}
                      onChange={(e) => updateShapeKey(key, e.target.value)}
                    />
                  </label>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}